import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  House,
  Files,
  File,
  FileText,
  CurrencyDollar,
  Hash,
  Stack,
  ChartPie,
  UsersThree,
  BookOpen,
  Users,
  Scroll,
  Note,
  Warning,
  LinkSimple,
  ArrowsLeftRight,
  ClipboardText,
  CheckCircle,
  Buildings,
  IconProps,
} from 'phosphor-react';
import { AuditoriaShell } from './AuditoriaShell';
import { useAuditoriaContext } from './useAuditoriaContext';
import { AuditoriaArea, NotaTipo } from './types';
import { DashboardScreen } from './screens/DashboardScreen';
import { DossieScreen } from './screens/DossieScreen';
import { FiscalScreen } from './screens/FiscalScreen';
import { NotasScreen } from './screens/NotasScreen';
import { ImpostosApuradosScreen } from './screens/ImpostosApuradosScreen';
import { ImpostosPorNotaScreen } from './screens/ImpostosPorNotaScreen';
import { AcumuladoresScreen } from './screens/AcumuladoresScreen';
import { SimplesScreen } from './screens/SimplesScreen';
import { ClientesFornScreen } from './screens/ClientesFornScreen';
import { ContabilScreen } from './screens/ContabilScreen';
import { PessoalScreen } from './screens/PessoalScreen';
import { SocietarioScreen } from './screens/SocietarioScreen';
import { ManuaisScreen } from './screens/ManuaisScreen';
import { AuditoriasScreen } from './screens/AuditoriasScreen';
import { CruzamentosScreen } from './screens/CruzamentosScreen';
import { IntegracaoScreen } from './screens/IntegracaoScreen';
import { ObrigacoesScreen } from './screens/ObrigacoesScreen';
import { FechamentoScreen } from './screens/FechamentoScreen';
import { EscritorioScreen } from './screens/EscritorioScreen';

export interface SubMeta {
  seg: string;
  name: string;
  eyebrow: string;
  desc: string;
  icon: React.ComponentType<IconProps>;
  cor: string;
  /** Tela multi-empresa (carteira) — esconde a ficha de empresa na topbar. */
  multi?: boolean;
}

/** Catálogo dos submódulos (espelha a antiga SubMenu). Dirige rotas + hub + sidebar. */
export const AUDITORIA_SUBMODULES: SubMeta[] = [
  {
    seg: 'dashboard',
    name: 'Dashboard',
    eyebrow: 'Visão geral',
    desc: 'Carteira ou empresa: faturamento, impostos, divergências e alertas.',
    icon: House,
    cor: '#1B88F5',
  },
  {
    seg: 'dossie',
    name: 'Dossiê 360',
    eyebrow: 'Visão geral',
    desc: 'Retrato completo da empresa, com análise por IA.',
    icon: Files,
    cor: '#7C5CE0',
  },
  {
    seg: 'fiscal',
    name: 'Fiscal — Visão Geral',
    eyebrow: 'Fiscal',
    desc: 'Resumo mensal de notas e impostos.',
    icon: File,
    cor: '#1B88F5',
  },
  {
    seg: 'notas',
    name: 'Notas Fiscais',
    eyebrow: 'Fiscal',
    desc: 'Todas as notas com filtros; clique abre o detalhe.',
    icon: FileText,
    cor: '#1B88F5',
  },
  {
    seg: 'impostos-apurados',
    name: 'Impostos Apurados',
    eyebrow: 'Fiscal',
    desc: 'Apuração mensal por imposto e ICMS débito × crédito.',
    icon: CurrencyDollar,
    cor: '#EFA221',
  },
  {
    seg: 'impostos-por-nota',
    name: 'Impostos por Nota',
    eyebrow: 'Fiscal',
    desc: 'Análise nota-a-nota: por nota, imposto, CFOP, UF e cliente.',
    icon: Hash,
    cor: '#EFA221',
  },
  {
    seg: 'acumuladores',
    name: 'Acumuladores',
    eyebrow: 'Fiscal',
    desc: 'Volume e saldo por acumulador fiscal.',
    icon: Stack,
    cor: '#1AA6B7',
  },
  {
    seg: 'simples',
    name: 'Simples Nacional',
    eyebrow: 'Fiscal',
    desc: 'Apuração do DAS e divergência faturamento × base SN.',
    icon: ChartPie,
    cor: '#27A567',
  },
  {
    seg: 'clientes-fornecedores',
    name: 'Clientes & Fornecedores',
    eyebrow: 'Fiscal',
    desc: 'Ranking, concentração e distribuição por UF.',
    icon: UsersThree,
    cor: '#2563EB',
  },
  {
    seg: 'contabil',
    name: 'Contábil',
    eyebrow: 'Contábil',
    desc: 'Balancete, DRE, indicadores e lançamentos.',
    icon: BookOpen,
    cor: '#7C3AED',
  },
  {
    seg: 'pessoal',
    name: 'Pessoal',
    eyebrow: 'Pessoal',
    desc: 'Folha, colaboradores, rescisões e guias.',
    icon: Users,
    cor: '#DB2777',
  },
  {
    seg: 'societario',
    name: 'Quadro Societário',
    eyebrow: 'Cadastral',
    desc: 'Sócios, participações e quotas.',
    icon: Scroll,
    cor: '#B45309',
  },
  {
    seg: 'manuais',
    name: 'Notas Manuais',
    eyebrow: 'Fiscal',
    desc: 'Quem lançou nota manual e se está integrada.',
    icon: Note,
    cor: '#7C5CE0',
  },
  {
    seg: 'auditorias',
    name: 'Auditorias',
    eyebrow: 'Auditoria',
    desc: 'Painel central de divergências e alertas.',
    icon: Warning,
    cor: '#E0483D',
  },
  {
    seg: 'cruzamentos',
    name: 'Cruzamentos por Área',
    eyebrow: 'Auditoria',
    desc: 'Apontamentos por área (Fiscal×Contábil, Folha…).',
    icon: LinkSimple,
    cor: '#1AA6B7',
  },
  {
    seg: 'integracao',
    name: 'Integração Fiscal-Contábil',
    eyebrow: 'Auditoria',
    desc: 'Notas pendentes e divergências de valor.',
    icon: ArrowsLeftRight,
    cor: '#1B88F5',
  },
  {
    seg: 'obrigacoes',
    name: 'Obrigações Acessórias',
    eyebrow: 'Auditoria',
    desc: 'Mapa de obrigações e registro do eCAC.',
    icon: ClipboardText,
    cor: '#9333EA',
  },
  {
    seg: 'fechamento',
    name: 'Acompanhamento de Fechamento',
    eyebrow: 'Gerencial',
    desc: 'Status de fechamento por empresa e responsável.',
    icon: CheckCircle,
    cor: '#27A567',
    multi: true,
  },
  {
    seg: 'escritorio',
    name: 'Gestão do Escritório',
    eyebrow: 'Gerencial',
    desc: 'Rankings de risco e produtividade da carteira.',
    icon: Buildings,
    cor: '#1B88F5',
    multi: true,
  },
];

const meta = (seg: string) => AUDITORIA_SUBMODULES.find(s => s.seg === seg) as SubMeta;
const shellProps = (s: SubMeta) => ({
  eyebrow: s.eyebrow,
  titulo: s.name,
  subtitulo: s.desc,
  icon: <s.icon size={24} weight="fill" />,
  cor: s.cor,
});

/* ----------------------------- Páginas-rota ----------------------------- */

export const AuditoriaDashboardPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('dashboard'))}>
      <DashboardScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        carteiraParams={ctx.carteiraParams}
        somenteAtivas={ctx.somenteAtivas}
        onToggleAtivas={ctx.onToggleAtivas}
        escopo={ctx.escopo}
        onEscopo={ctx.onEscopo}
        onOpenEmpresa={ctx.onOpenEmpresaById}
        loadingEmpresa={ctx.loadingEmpresa}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaDossiePage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('dossie'))}>
      <DossieScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onScreen={ctx.onScreen}
        onOpenAuditoria={ctx.onOpenAuditoria}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaFiscalPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('fiscal'))}>
      <FiscalScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onScreen={ctx.onScreen}
        onOpenNotasMes={ctx.onOpenNotasMes}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaNotasPage = () => {
  const ctx = useAuditoriaContext();
  const [sp] = useSearchParams();
  const busca = sp.get('busca') || undefined;
  const tipo = (sp.get('tipo') as NotaTipo) || undefined;
  return (
    <AuditoriaShell {...shellProps(meta('notas'))}>
      <NotasScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        buscaInicial={busca}
        tipoInicial={tipo}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaImpostosApuradosPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('impostos-apurados'))}>
      <ImpostosApuradosScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaImpostosPorNotaPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('impostos-por-nota'))}>
      <ImpostosPorNotaScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onOpenNotas={ctx.onOpenNotas}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaAcumuladoresPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('acumuladores'))}>
      <AcumuladoresScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaSimplesPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('simples'))}>
      <SimplesScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onScreen={ctx.onScreen}
        onOpenAuditoria={ctx.onOpenAuditoria}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaClientesFornPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('clientes-fornecedores'))}>
      <ClientesFornScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onOpenNotas={ctx.onOpenNotas}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaContabilPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('contabil'))}>
      <ContabilScreen empresa={ctx.empresa} periodo={ctx.periodoParams} onScreen={ctx.onScreen} />
    </AuditoriaShell>
  );
};

export const AuditoriaPessoalPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('pessoal'))}>
      <PessoalScreen empresa={ctx.empresa} periodo={ctx.periodoParams} />
    </AuditoriaShell>
  );
};

export const AuditoriaSocietarioPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('societario'))}>
      <SocietarioScreen empresa={ctx.empresa} />
    </AuditoriaShell>
  );
};

export const AuditoriaManuaisPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('manuais'))}>
      <ManuaisScreen empresa={ctx.empresa} periodo={ctx.periodoParams} onScreen={ctx.onScreen} />
    </AuditoriaShell>
  );
};

export const AuditoriaAuditoriasPage = () => {
  const ctx = useAuditoriaContext();
  const [sp] = useSearchParams();
  const area = (sp.get('area') as AuditoriaArea) || undefined;
  const focusKey = sp.get('focus') || undefined;
  return (
    <AuditoriaShell {...shellProps(meta('auditorias'))}>
      <AuditoriasScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        areaInicial={area}
        focusKey={focusKey}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaCruzamentosPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('cruzamentos'))}>
      <CruzamentosScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onOpenAuditoria={ctx.onOpenAuditoria}
        onScreen={ctx.onScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaIntegracaoPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('integracao'))}>
      <IntegracaoScreen empresa={ctx.empresa} periodo={ctx.periodoParams} onScreen={ctx.onScreen} />
    </AuditoriaShell>
  );
};

export const AuditoriaObrigacoesPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('obrigacoes'))}>
      <ObrigacoesScreen
        empresa={ctx.empresa}
        periodo={ctx.periodoParams}
        onOpenEmpresaScreen={ctx.onOpenEmpresaScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaFechamentoPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('fechamento'))}>
      <FechamentoScreen
        onOpenEmpresa={ctx.onOpenEmpresaById}
        onOpenEmpresaScreen={ctx.onOpenEmpresaScreen}
      />
    </AuditoriaShell>
  );
};

export const AuditoriaEscritorioPage = () => {
  const ctx = useAuditoriaContext();
  return (
    <AuditoriaShell {...shellProps(meta('escritorio'))}>
      <EscritorioScreen
        periodo={ctx.periodoParams}
        onOpenEmpresa={ctx.onOpenEmpresaById}
        onOpenEmpresaScreen={ctx.onOpenEmpresaScreen}
      />
    </AuditoriaShell>
  );
};
